import { useState, useRef } from "react";
import { 
  Box, 
  Button,
  Typography,
  Tooltip
} from "@mui/material";
import { 
  Download, 
  Refresh
} from "@mui/icons-material";
import CustomSnackbar from "../components/Message.Component";
import { usePriceReferences } from "../../client/hooks/products/products.queries";
import ProductReferencesTable from "./components/referencias-preco/ProductReferencesTable";
import ProductReferenceDetails from "./components/referencias-preco/ProductReferenceDetails";

export default function ReferenciasPrecoPage() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState({
    search: '',
    minPrice: '',
    maxPrice: ''
  });
  const [alert, setAlert] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const detailsRef = useRef(null);

  // Hook para buscar lista de produtos
  const listParams = {
    page: page,
    limit: limit,
    ...(filters.search && { item_id: filters.search }),
    ...(filters.minPrice && { minPrice: filters.minPrice }),
    ...(filters.maxPrice && { maxPrice: filters.maxPrice })
  };
  
  const { 
    data: priceReferencesResponse, 
    isLoading, 
    refetch 
  } = usePriceReferences(listParams, true, {
    keepPreviousData: true,
    staleTime: 5000
  });
  
  // Hook para buscar detalhes do produto selecionado
  const detailsParams = {
    page: 1,
    limit: 1,
    ...(selectedProduct?.item_id && { item_id: selectedProduct.item_id })
  };
  
  const { 
    data: selectedProductResponse, 
    isLoading: isLoadingDetails,
    refetch: refetchDetails
  } = usePriceReferences(detailsParams, !!selectedProduct);

  // Processar dados da lista de produtos
  const processedData = priceReferencesResponse?.priceReferences || [];
  const pagination = priceReferencesResponse?.pagination || {
    currentPage: page,
    limit: limit,
    total: 0,
    totalPages: 0
  };
  
  // Processar dados do produto selecionado
  const processSelectedProductData = (response) => {
    if (!response || !response.priceReferences || response.priceReferences.length === 0) {
      return null;
    }

    const reference = response.priceReferences[0];
    const competitorData = reference.metadata?.graph || [];
    
    return {
      item_id: reference.item_id,
      product_title: competitorData[0]?.info?.title || `Produto ${reference.item_id}`,
      current_price: reference.current_price,
      suggested_price: reference.suggested_price,
      lowest_price: reference.lowest_price,
      percent_difference: reference.percent_difference,
      ratio: reference.ratio,
      status: reference.status,
      competitors: competitorData,
      competitors_count: reference.metadata?.compared_values || competitorData.length,
      last_updated: reference.last_updated
    };
  };
  
  const selectedProductData = processSelectedProductData(selectedProductResponse);

  const showAlert = (msg, sev = "info") => {
    setMessage(msg);
    setSeverity(sev);
    setAlert(true);
  };

  const handleExport = () => {
    showAlert("Exportação iniciada com sucesso!", "success");
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      if (selectedProduct) {
        await refetchDetails();
      }
      showAlert("Dados atualizados com sucesso!", "success");
    } catch (error) {
      showAlert("Erro ao atualizar dados.", "warning");
    }
  };
  
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    
    // Smooth scroll to details
    setTimeout(() => {
      if (detailsRef.current) {
        detailsRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };
  
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  
  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };
  
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, bgcolor: '#fafafa', minHeight: '100vh' }}>
      <CustomSnackbar
        open={alert}
        onClose={() => setAlert(false)}
        severity={severity}
        message={message}
      />

      {/* Header */}
      <Box sx={{ 
        mb: 3,
        pb: 3,
        borderBottom: '2px solid #f5f5f5'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 3
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#333',
                  letterSpacing: '-0.025em'
                }}
              >
                Referências de Preço
              </Typography>
              <Box 
                component="img"
                src="https://http2.mlstatic.com/storage/developers-site-cms-admin/DevImgs/191761039894-Mercado-Libre.png"
                alt="Mercado Livre"
                sx={{
                  height: 48,
                  width: 'auto',
                  objectFit: 'contain'
                }}
              />
            </Box>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                fontSize: '0.95rem',
                mt: 0.8
              }}
            >
              Monitoramento de preços e análise competitiva em tempo real no marketplace
            </Typography>
          </Box>
          <Box sx={{ 
            mt: { xs: 3, md: 0 }, 
            display: 'flex', 
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            <Button 
              variant="outlined" 
              startIcon={<Refresh />}
              onClick={handleRefresh}
              sx={{ 
                borderColor: '#dee2e6',
                color: '#495057',
                bgcolor: 'white',
                fontWeight: 500,
                px: 3,
                py: 1,
                '&:hover': {
                  borderColor: '#adb5bd',
                  bgcolor: '#f8f9fa'
                }
              }}
            >
              Atualizar
            </Button>
            <Tooltip title="Em Desenvolvimento" arrow placement="top">
              <span>
                <Button 
                  variant="contained" 
                  startIcon={<Download />}
                  disabled
                  sx={{ 
                    bgcolor: '#e0e0e0',
                    color: '#9e9e9e',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    '&.Mui-disabled': {
                      bgcolor: '#e0e0e0',
                      color: '#9e9e9e'
                    }
                  }}
                >
                  Exportar
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Product List */}
      <ProductReferencesTable 
        data={processedData}
        isLoading={isLoading}
        pagination={pagination}
        onSelectProduct={handleSelectProduct}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onFiltersChange={handleFiltersChange}
        filters={filters}
        selectedProduct={selectedProduct}
      />

      {/* Product Details */}
      <div ref={detailsRef}>
        <ProductReferenceDetails 
          productData={selectedProductData}
          isLoading={isLoadingDetails}
        />
      </div>
    </Box>
  );
}